import { useState } from 'react';
import {
  Button,
  H2,
  Input,
  Paragraph,
  Spinner,
  TextArea,
  XStack,
  YStack,
} from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import type { ListItem } from '@mrrmlab/shared-types';
import {
  useAddListItem,
  useDeleteListItem,
  useList,
  useUpdateListItem,
} from '../hooks';

export interface NotesListScreenProps {
  listId: string;
  onBack?: () => void;
}

/**
 * Detail view for a `notes`-typed list: items are notes with `title` + `body`.
 * Renders each note as an expandable card; tap "Edit" to update, trash icon to
 * delete. Ordering mirrors the shopping screen: by `position` then `createdAt`.
 */
export function NotesListScreen({ listId, onBack }: NotesListScreenProps) {
  const query = useList(listId);
  const addItem = useAddListItem(listId);
  const updateItem = useUpdateListItem(listId);
  const deleteItem = useDeleteListItem(listId);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const canSubmit = title.trim().length > 0;

  const submit = () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;
    addItem.mutate(
      {
        data: {
          type: 'notes',
          title: trimmedTitle,
          body: body.trim(),
        },
      },
      {
        onSuccess: () => {
          setTitle('');
          setBody('');
        },
      },
    );
  };

  if (query.isPending) {
    return (
      <Screen>
        <Spinner />
      </Screen>
    );
  }

  if (query.isError) {
    return (
      <Screen>
        <Paragraph color="$red10">Failed to load list: {query.error.message}</Paragraph>
        {onBack ? <Button onPress={onBack}>Back</Button> : null}
      </Screen>
    );
  }

  const list = query.data;
  if (list.type !== 'notes') {
    return (
      <Screen>
        <Paragraph color="$red10">
          This screen only handles `notes` lists, but this list is `{list.type}`.
        </Paragraph>
        {onBack ? <Button onPress={onBack}>Back</Button> : null}
      </Screen>
    );
  }

  const sortedItems = [...list.items].sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position;
    return a.createdAt.localeCompare(b.createdAt);
  });

  return (
    <Screen scrollable>
      <XStack alignItems="center" gap="$3">
        {onBack ? (
          <Button size="$2" chromeless onPress={onBack}>
            ← Back
          </Button>
        ) : null}
        <H2 flex={1}>{list.title}</H2>
      </XStack>

      <YStack gap="$2">
        <Input
          placeholder="Note title"
          value={title}
          onChangeText={setTitle}
          onSubmitEditing={submit}
        />
        <TextArea
          placeholder="Body (optional)"
          value={body}
          onChangeText={setBody}
          numberOfLines={4}
        />
        <Button disabled={addItem.isPending || !canSubmit} onPress={submit}>
          Add note
        </Button>
      </YStack>

      {sortedItems.length === 0 ? (
        <Paragraph opacity={0.7}>No notes yet.</Paragraph>
      ) : (
        <YStack gap="$3">
          {sortedItems.map((item) => (
            <NoteCard
              key={item.id}
              item={item}
              onSave={(next) =>
                updateItem.mutate({
                  itemId: item.id,
                  dto: { data: { type: 'notes', ...next } },
                })
              }
              onDelete={() => deleteItem.mutate(item.id)}
              busy={updateItem.isPending || deleteItem.isPending}
            />
          ))}
        </YStack>
      )}
    </Screen>
  );
}

interface NoteCardProps {
  item: ListItem;
  onSave: (next: { title: string; body: string }) => void;
  onDelete: () => void;
  busy: boolean;
}

function NoteCard({ item, onSave, onDelete, busy }: NoteCardProps) {
  const noteData = item.data.type === 'notes' ? item.data : null;
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(noteData?.title ?? '');
  const [body, setBody] = useState(noteData?.body ?? '');

  if (!noteData) return null;

  const startEdit = () => {
    setTitle(noteData.title);
    setBody(noteData.body);
    setEditing(true);
  };

  const save = () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;
    onSave({ title: trimmedTitle, body: body.trim() });
    setEditing(false);
  };

  return (
    <YStack
      gap="$2"
      padding="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
      backgroundColor="$background"
    >
      {editing ? (
        <>
          <Input value={title} onChangeText={setTitle} placeholder="Title" />
          <TextArea value={body} onChangeText={setBody} numberOfLines={4} />
          <XStack gap="$2" justifyContent="flex-end">
            <Button size="$2" chromeless onPress={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="$2" disabled={busy || title.trim().length === 0} onPress={save}>
              Save
            </Button>
          </XStack>
        </>
      ) : (
        <>
          <XStack alignItems="center" gap="$2">
            <Paragraph flex={1} fontSize="$5" fontWeight="600">
              {noteData.title}
            </Paragraph>
            <Button size="$2" chromeless onPress={startEdit}>
              Edit
            </Button>
            <Button size="$2" chromeless disabled={busy} onPress={onDelete}>
              ✕
            </Button>
          </XStack>
          {noteData.body ? (
            <Paragraph opacity={0.8}>{noteData.body}</Paragraph>
          ) : null}
        </>
      )}
    </YStack>
  );
}
